import { Box, Stack, Text, useMantineTheme } from '@mantine/core';
import { CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { ThecData } from '../types';
import { latest, pctChange, previous } from '../helpers';
import { getMultiGoalStatus, getThresholdGoalState } from '../../statusUtils';
import { CardShell } from './CardShell';
import { MetricRow } from './MetricRow';

export function ProgramCompletionCard({ data }: { data: ThecData }) {
  const theme = useMantineTheme();
  const completions = data.completions;
  const participants = data.participants;
  const cur = latest(completions);
  const prev = previous(completions);

  const totalParticipants = data.participantsAllTimeTotal;
  const totalGraduates = data.completionsAllTimeTotal;
  const completionRate = totalParticipants > 0 ? (totalGraduates / totalParticipants) * 100 : 0;

  const goalGraduates = 100;
  const goalCompletionRate = 85;

  const parseQuarterLabel = (label: string): { year: number; quarter: 1 | 2 | 3 | 4 } | null => {
    const m1 = label.match(/^Q([1-4])\s+(\d{4})$/);
    if (m1) return { year: Number(m1[2]), quarter: Number(m1[1]) as 1 | 2 | 3 | 4 };
    const m2 = label.match(/^(\d{4})\s+Q([1-4])$/);
    if (m2) return { year: Number(m2[1]), quarter: Number(m2[2]) as 1 | 2 | 3 | 4 };
    return null;
  };

  const curMeta = parseQuarterLabel(cur.quarter);
  const curYear = curMeta?.year ?? null;
  const curQuarterNum = curMeta?.quarter ?? null;

  const ytdGraduates =
    curYear === null || curQuarterNum === null
      ? null
      : completions
          .filter((p) => {
            const m = parseQuarterLabel(p.quarter);
            return m?.year === curYear && m.quarter <= curQuarterNum;
          })
          .reduce((acc, p) => acc + p.completed, 0);

  const prevYearYtdGraduates =
    curYear === null || curQuarterNum === null
      ? null
      : completions
          .filter((p) => {
            const m = parseQuarterLabel(p.quarter);
            return m?.year === curYear - 1 && m.quarter <= curQuarterNum;
          })
          .reduce((acc, p) => acc + p.completed, 0);

  const yoyGraduatesPct =
    ytdGraduates === null || prevYearYtdGraduates === null ? null : pctChange(ytdGraduates, prevYearYtdGraduates);

  const arrow = (n: number): '↑' | '↓' | '' => (n > 0 ? '↑' : n < 0 ? '↓' : '');
  const yoyGraduatesText =
    yoyGraduatesPct === null ? '—' : arrow(yoyGraduatesPct) ? `${arrow(yoyGraduatesPct)}${Math.abs(yoyGraduatesPct).toFixed(2)}% YoY` : '0.00% YoY';

  const qoqGraduatesPct = pctChange(cur.completed, prev?.completed ?? null);
  const qoqGraduatesText =
    qoqGraduatesPct === null ? '—' : arrow(qoqGraduatesPct) ? `${arrow(qoqGraduatesPct)}${Math.abs(qoqGraduatesPct).toFixed(2)}% QoQ` : '0.00% QoQ';

  const status = getMultiGoalStatus([
    getThresholdGoalState({ value: totalGraduates, goal: goalGraduates, direction: 'min' }),
    getThresholdGoalState({ value: completionRate, goal: goalCompletionRate, direction: 'min' }),
  ]);

  const tooltipShellStyle = {
    backgroundColor: theme.colorScheme === 'dark' ? '#000' : '#fff',
    border: `1px solid ${theme.colorScheme === 'dark' ? '#333' : '#ddd'}`,
    borderRadius: 10,
    color: theme.colorScheme === 'dark' ? '#fff' : '#000',
    padding: '10px 12px',
    maxWidth: 320,
    whiteSpace: 'normal' as const,
    wordBreak: 'break-word' as const,
  } as const;

  let cumulative = 0;
  const chartData = completions.map((p) => {
    cumulative += p.completed;
    return { quarter: p.quarter, completed: p.completed, cumulative };
  });
  const maxCumulative = Math.max(goalGraduates, ...chartData.map((p) => p.cumulative));

  const participantsByQuarter = new Map(participants.map((p) => [p.quarter, p.participantsStarted]));
  let cumulativeParticipants = 0;
  let cumulativeCompletions = 0;
  const completionRateData = completions.map((p) => {
    cumulativeCompletions += p.completed;
    cumulativeParticipants += participantsByQuarter.get(p.quarter) ?? 0;
    const rate = cumulativeParticipants > 0 ? (cumulativeCompletions / cumulativeParticipants) * 100 : 0;
    return {
      quarter: p.quarter,
      rate,
      cumulativeCompletions,
      cumulativeParticipants,
      completedThisQuarter: p.completed,
    };
  });
  const maxRate = Math.max(goalCompletionRate, ...completionRateData.map((p) => p.rate));

  return (
    <CardShell
      title="Program Completion"
      subtitle={cur.quarter}
      statusLabel={status.label}
      statusColor={status.color}
      statusTooltip={status.tooltip}
    >
      <Stack spacing="sm">
        <MetricRow
          items={[
            {
              label: 'This Quarter',
              labelTooltip: 'Unique individuals who completed the program this quarter.',
              value: (
                <Text size="lg" weight={800}>
                  {cur.completed}
                </Text>
              ),
              sublabel: qoqGraduatesText,
            },
            {
              label: 'YTD',
              labelTooltip: 'Calendar year to date graduates (sum of completions in the current year through this quarter).',
              value: (
                <Text size="lg" weight={800}>
                  {ytdGraduates === null ? '—' : ytdGraduates}
                </Text>
              ),
              sublabel: yoyGraduatesText,
            },
            {
              label: 'Graduates (All-Time)',
              labelTooltip: `Total graduates all-time (includes periods not shown). Goal: ${goalGraduates} by end of performance period.`,
              value: (
                <Text size="lg" weight={800}>
                  {totalGraduates}
                </Text>
              ),
              sublabel: `Goal ${goalGraduates}`,
            },
            {
              label: 'Completion Rate (All-Time)',
              labelTooltip: `Percent of participants who completed the program (all-time). Goal: ${goalCompletionRate}% completion rate.`,
              value: (
                <Text size="lg" weight={800}>
                  {Math.round(completionRate)}%
                </Text>
              ),
              sublabel: `Goal ${goalCompletionRate}%`,
            },
          ]}
        />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: '1rem',
            alignItems: 'start',
            marginTop: '0.75rem',
            '@media (max-width: 980px)': {
              gridTemplateColumns: '1fr',
            },
          }}
        >
          <Box>
            <Text size="xs" color="dimmed" weight={700} sx={{ letterSpacing: 0.2, marginBottom: '0.5rem' }}>
              Cumulative Program Completions
            </Text>
            <Box sx={{ height: 170 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.18} vertical={false} />
                  <XAxis
                    dataKey="quarter"
                    tick={{ fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    interval="preserveStartEnd"
                    minTickGap={12}
                  />
                  <YAxis
                    orientation="right"
                    tick={{ fontSize: 11 }}
                    allowDecimals={false}
                    axisLine={false}
                    tickLine={false}
                    width={44}
                    tickMargin={8}
                    domain={[0, maxCumulative]}
                  />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (!active || !payload || payload.length === 0) return null;
                      const p: any = payload[0]?.payload;
                      const v = p?.cumulative;
                      const quarterValue = p?.completed;
                      return (
                        <div style={tooltipShellStyle as any}>
                          <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 12, opacity: 0.9 }}>{label}</div>
                          <div style={{ fontSize: 12, lineHeight: 1.35 }}>
                            <div style={{ fontWeight: 900, fontSize: 14 }}>{typeof v === 'number' ? v : '—'} Total</div>
                            <div style={{ opacity: 0.9 }}>
                              {typeof quarterValue === 'number' ? quarterValue : '—'} completed this quarter
                            </div>
                          </div>
                        </div>
                      );
                    }}
                  />
                  <ReferenceLine y={goalGraduates} stroke="#fa5252" strokeDasharray="4 4" />
                  <Line
                    type="monotone"
                    dataKey="cumulative"
                    name="Cumulative Graduates"
                    stroke={theme.colors.blue[6]}
                    strokeWidth={2}
                    dot={(dotProps: any) => {
                      const pointKey = dotProps?.payload?.quarter ?? dotProps?.index ?? `${dotProps?.cx}-${dotProps?.cy}`;
                      return (
                        <circle
                          key={`thec-completion-cumulative-dot-${pointKey}`}
                          cx={dotProps.cx}
                          cy={dotProps.cy}
                          r={2}
                          fill={theme.colors.blue[6]}
                          stroke={theme.colorScheme === 'dark' ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.8)'}
                          strokeWidth={1.5}
                        />
                      );
                    }}
                    activeDot={{ r: 4 }}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Box>

          <Box>
            <Text size="xs" color="dimmed" weight={700} sx={{ letterSpacing: 0.2, marginBottom: '0.5rem' }}>
              All-Time Completion Rate
            </Text>
            <Box sx={{ height: 170 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={completionRateData}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.18} vertical={false} />
                  <XAxis
                    dataKey="quarter"
                    tick={{ fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    interval="preserveStartEnd"
                    minTickGap={12}
                  />
                  <YAxis
                    orientation="right"
                    tick={{ fontSize: 11 }}
                    allowDecimals={false}
                    axisLine={false}
                    tickLine={false}
                    width={44}
                    tickMargin={8}
                    domain={[0, Math.min(100, Math.ceil(maxRate))]}
                    tickFormatter={(value) => `${value}%`}
                  />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (!active || !payload || payload.length === 0) return null;
                      const p: any = payload[0]?.payload;
                      if (!p) return null;
                      return (
                        <div style={tooltipShellStyle as any}>
                          <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 12, opacity: 0.9 }}>{label}</div>
                          <div style={{ fontSize: 12, lineHeight: 1.35 }}>
                            <div style={{ fontWeight: 900, fontSize: 14 }}>{Math.round(p.rate)}% Completion Rate</div>
                            <div style={{ opacity: 0.9 }}>
                              {p.cumulativeCompletions} completions out of {p.cumulativeParticipants} participants
                            </div>
                          </div>
                        </div>
                      );
                    }}
                  />
                  <ReferenceLine y={goalCompletionRate} stroke="#fa5252" strokeDasharray="4 4" ifOverflow="extendDomain" />
                  <Line
                    type="monotone"
                    dataKey="rate"
                    name="Completion Rate"
                    stroke={theme.colors.blue[6]}
                    strokeWidth={2}
                    dot={(dotProps: any) => {
                      const pointKey = dotProps?.payload?.quarter ?? dotProps?.index ?? `${dotProps?.cx}-${dotProps?.cy}`;
                      return (
                        <circle
                          key={`thec-completion-rate-dot-${pointKey}`}
                          cx={dotProps.cx}
                          cy={dotProps.cy}
                          r={2}
                          fill={theme.colors.blue[6]}
                          stroke={theme.colorScheme === 'dark' ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.8)'}
                          strokeWidth={1.5}
                        />
                      );
                    }}
                    activeDot={{ r: 4 }}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        </Box>
      </Stack>
    </CardShell>
  );
}
